/**
 * Wizard→event Velo handoff. The /create-event wizard interview lives in a
 * COMPANY-scoped, ephemeral conversation — the freshly created event's Velo
 * is a different scope and would start blind. At Create time the wizard
 * stashes a handoff here (the server-side interview brief + what the starter
 * package actually created, incl. workflow enabled/disabled states); on
 * arrival in the event the AppBar Velo button gets a badge, the empty chat
 * shows a dismissible handoff notice, and the FIRST message of the event's
 * Velo conversation carries the composed brief as `handoffBrief` — the
 * backend pins it on the new conversation and injects it into the system
 * prompt every turn.
 *
 * sessionStorage (like the direction-package results): tab-scoped, one-shot,
 * survives the create→event navigation but not a new tab. Best-effort — a
 * blocked storage just means no handoff, never a throw.
 */

export interface VeloHandoffWorkflow {
  /** Already-localized display name (from the direction pack). */
  label: string;
  /** Created enabled (event-driven flows) vs disabled (scheduled broadcasts). */
  enabled: boolean;
}

export interface VeloHandoffItem {
  kind: string;
  label: string;
  ok: boolean;
}

export interface VeloHandoff {
  eventId: string;
  eventName: string;
  /** Localized direction name, null when no direction package was used. */
  directionName: string | null;
  /** The wizard conversation's durable interview brief (server-side), if any. */
  brief: string | null;
  /** Workflows the package created, with their real enabled state. */
  workflows: VeloHandoffWorkflow[];
  /** Package items (website/emails/offering/…) with their outcome. */
  items: VeloHandoffItem[];
  createdAt: string;
}

const STORAGE_PREFIX = "veltric-velo-handoff:";
/** Mirrors the backend's @Size(max = 4000) on AiChatRequest.handoffBrief. */
const MAX_HANDOFF_CHARS = 4000;

function storageKey(eventId: string): string {
  return `${STORAGE_PREFIX}${eventId}`;
}

export function storeVeloHandoff(handoff: VeloHandoff): void {
  try {
    sessionStorage.setItem(storageKey(handoff.eventId), JSON.stringify(handoff));
  } catch {
    /* best-effort */
  }
}

export function readVeloHandoff(eventId: string): VeloHandoff | null {
  try {
    const raw = sessionStorage.getItem(storageKey(eventId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as VeloHandoff;
    return parsed && parsed.eventId === eventId ? parsed : null;
  } catch {
    return null;
  }
}

export function clearVeloHandoff(eventId: string): void {
  try {
    sessionStorage.removeItem(storageKey(eventId));
  } catch {
    /* best-effort */
  }
}

/**
 * The text sent to the backend as `handoffBrief` on the event conversation's
 * first message. English wrapper labels on purpose (the system prompt is
 * English); the brief itself stays in the interview's language.
 */
export function composeVeloHandoffBrief(handoff: VeloHandoff): string {
  const parts: string[] = [];
  if (handoff.brief && handoff.brief.trim()) {
    parts.push(`Interview brief from the create-event wizard:\n${handoff.brief.trim()}`);
  }
  if (handoff.directionName) {
    // Since the Setup Journey (2026-07-08) NOTHING is built at create — the
    // direction only shapes proposals the user resolves on the Setup Hub.
    // Legacy handoffs (items/workflows non-empty) no longer occur; the plan
    // itself is readable via the get_setup_plan tool.
    parts.push(
      `Direction "${handoff.directionName}" was chosen in the wizard. Its starter content ` +
        `(emails, workflows, offering, website) exists only as PROPOSALS in the event's ` +
        `setup journey — nothing was built at create. The user builds, adjusts or skips ` +
        `each section on the Setup page; read the plan with get_setup_plan before ` +
        `designing content for one of those sections.`,
    );
  }
  return parts.join("\n\n").slice(0, MAX_HANDOFF_CHARS);
}
